import type { RiserConfig } from './riserConfig';
import { layoutRisers, riserViewBox, defaultRisers } from './riserConfig';
import { STAGE_SVG_GRAY } from './StageIcon';

export interface RiserGraphicProps {
  config: RiserConfig;
  color: string;
  /** Pixel box the graphic must fill (full element size on canvas). */
  boxW: number;
  boxH: number;
}

/**
 * Top-down row of riser blocks. Each block is a rounded rect, outlined, with
 * its W×D×H dimension label. The SVG viewBox uses the real W×D ratio (via
 * riserViewBox) and is stretched to fill boxW×boxH, so a 2×1m element draws a
 * 2:1 rectangle that fills the whole element box. Risers have no StagePlotPro
 * PNG, so they render in STAGE_SVG_GRAY (neutral grayscale) to stay consistent
 * with the other PNG-based elements — the `color` prop is intentionally ignored.
 */
export function RiserGraphic({ config, boxW, boxH }: RiserGraphicProps) {
  // Render defense: a legacy/invalid riser config must never crash the canvas.
  const safe = config && typeof config === 'object' && typeof config.count === 'number' ? config : defaultRisers();
  const rects = layoutRisers(safe);
  const vb = riserViewBox(safe);
  return (
    <svg
      width={boxW}
      height={boxH}
      viewBox={`0 0 ${vb.w} ${vb.h}`}
      preserveAspectRatio="none"
      style={{ color: STAGE_SVG_GRAY, display: 'block' }}
      role="img"
      aria-label={`Praticable (${safe.count})`}
    >
      {rects.map((r) => (
        <g key={r.i}>
          <rect
            x={r.x}
            y={r.y}
            width={r.w}
            height={r.h}
            rx={2}
            fill={STAGE_SVG_GRAY}
            stroke="#0004"
            strokeWidth={1}
          />
          <rect
            x={r.x}
            y={r.y}
            width={r.w}
            height={Math.min(4, r.h * 0.3)}
            rx={2}
            fill="#0003"
          />
        </g>
      ))}
    </svg>
  );
}

export default RiserGraphic;
